/**
 * Darstellung von Datum und Uhrzeit (§37).
 *
 * Verbindlich: Datumsformat `TT.MM.JJJJ`, Uhrzeit im 24-Stunden-Format. Gespeichert wird
 * ausschließlich in UTC; angezeigt wird in der Anzeigezeitzone.
 *
 * **Warum das im gemeinsamen Paket liegt:** §37 verlangt für PDF und CSV die lokale Anzeigezeit,
 * für JSON dagegen UTC. Die Anzeigezeit entsteht damit an zwei Orten — in der Oberfläche und in
 * den serverseitig erzeugten Dateien. Zwei Implementierungen könnten auseinanderlaufen und
 * dieselbe Abstimmung im Bildschirm anders datieren als im Protokoll-PDF.
 *
 * Die Zeitzone ist über die Umgebungsvariable `TZ` konfigurierbar. Das Backend übergibt sie
 * ausdrücklich; die Oberfläche kennt sie bisher nicht und verwendet die Vorgabe.
 */
/** Anzeigezeitzone als Vorgabe (§37). Über die Umgebungsvariable `TZ` überschreibbar. */
export const DEFAULT_DISPLAY_TIMEZONE = 'Europe/Berlin';
/** Ausgabe für einen fehlenden oder unbrauchbaren Zeitwert. */
export const ZEIT_PLATZHALTER = '—';
/**
 * `Intl.DateTimeFormat` ist teuer in der Erzeugung und wird je Zeitzone einmal angelegt.
 *
 * Bei einem Export mit vielen Zeilen entstünde sonst je Zeile ein neuer Formatierer.
 */
const formatiererZwischenspeicher = new Map();
function formatierer(schluessel, erzeuge) {
    let vorhanden = formatiererZwischenspeicher.get(schluessel);
    if (vorhanden === undefined) {
        vorhanden = erzeuge();
        formatiererZwischenspeicher.set(schluessel, vorhanden);
    }
    return vorhanden;
}
/**
 * Formatiert einen UTC-Zeitpunkt nach ISO 8601 als `TT.MM.JJJJ, HH:MM` (§37).
 *
 * @param isoZeitpunkt Zeitpunkt in UTC, wie er in der Datenbank steht.
 * @param zeitzone Anzeigezeitzone; ohne Angabe gilt {@link DEFAULT_DISPLAY_TIMEZONE}.
 * @returns die formatierte Angabe oder {@link ZEIT_PLATZHALTER}, wenn der Wert unbrauchbar ist.
 */
export function formatiereZeitpunkt(isoZeitpunkt, zeitzone = DEFAULT_DISPLAY_TIMEZONE) {
    if (isoZeitpunkt === null || isoZeitpunkt === undefined) {
        return ZEIT_PLATZHALTER;
    }
    const zeitpunkt = new Date(isoZeitpunkt);
    if (Number.isNaN(zeitpunkt.getTime())) {
        return ZEIT_PLATZHALTER;
    }
    return formatierer(`zeitpunkt:${zeitzone}`, () => new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: zeitzone,
    })).format(zeitpunkt);
}
/**
 * Wie {@link formatiereZeitpunkt}, zusätzlich mit Sekunden: `TT.MM.JJJJ, HH:MM:SS`.
 *
 * Vorgesehen für die Kopfzeile eines Exports — dort kann eine Datei innerhalb derselben Minute
 * mehrfach erzeugt werden, und der Zeitstempel soll die Fassungen unterscheiden.
 */
export function formatiereZeitpunktLang(isoZeitpunkt, zeitzone = DEFAULT_DISPLAY_TIMEZONE) {
    if (isoZeitpunkt === null || isoZeitpunkt === undefined) {
        return ZEIT_PLATZHALTER;
    }
    const zeitpunkt = new Date(isoZeitpunkt);
    if (Number.isNaN(zeitpunkt.getTime())) {
        return ZEIT_PLATZHALTER;
    }
    return formatierer(`zeitpunkt-lang:${zeitzone}`, () => new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: zeitzone,
    })).format(zeitpunkt);
}
/**
 * Formatiert ein reines Datum `JJJJ-MM-TT` als `TT.MM.JJJJ` (§37).
 *
 * Ausgewertet wird in **UTC**, unabhängig von der Anzeigezeitzone: ein Veranstaltungsdatum ist
 * ein Kalendertag ohne Uhrzeit und darf durch eine Zeitzonenverschiebung nicht auf den Vortag
 * rutschen.
 */
export function formatiereDatum(isoDatum) {
    if (isoDatum === null || isoDatum === undefined) {
        return ZEIT_PLATZHALTER;
    }
    const zeitpunkt = new Date(`${isoDatum}T00:00:00.000Z`);
    if (Number.isNaN(zeitpunkt.getTime())) {
        return ZEIT_PLATZHALTER;
    }
    return formatierer('datum', () => new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
    })).format(zeitpunkt);
}
/**
 * Normalisiert einen Zeitpunkt für den JSON-Export auf UTC nach ISO 8601 (§37).
 *
 * §27.1 verlangt „eindeutige UTC-Zeitpunkte". Die Datenbank enthält bereits ISO-Werte, aber ohne
 * diese Normalisierung hinge ihre Schreibweise davon ab, wie sie einmal geschrieben wurden.
 *
 * @returns den Zeitpunkt als `JJJJ-MM-TTTHH:MM:SS.sssZ` oder `null`.
 */
export function alsUtcZeitpunkt(isoZeitpunkt) {
    if (isoZeitpunkt === null || isoZeitpunkt === undefined) {
        return null;
    }
    const zeitpunkt = new Date(isoZeitpunkt);
    if (Number.isNaN(zeitpunkt.getTime())) {
        return null;
    }
    return zeitpunkt.toISOString();
}
//# sourceMappingURL=zeit.js.map